<template>
  <div class="pg-hand">
    <!-- Instructions -->
    <div class="p-field">
      <label class="p-field__label">안내 문구 (Instructions)</label>
      <textarea
        v-model="sectionData.content.instructions"
        class="p-input p-input--area"
        rows="2"
        placeholder="손글씨 연습에 대한 안내 문구를 입력하세요 (예: 아래 글자를 따라 써보세요)"
      />
    </div>

    <!-- Placeholder Settings -->
    <div class="pg-hand__section">
      <div class="pg-hand__section-label">플레이스홀더 설정 (Placeholder Settings)</div>
      <div class="pg-hand__settings">
        <div class="p-form-grid p-form-grid--2">
          <div class="p-field">
            <label class="p-field__label">플레이스홀더 스타일 (Placeholder Style)</label>
            <select v-model="sectionData.content.placeholderStyle" class="p-input">
              <option value="outline">외곽선 (Outline)</option>
              <option value="dotted">점선 (Dotted)</option>
              <option value="light">연한 글자 (Light)</option>
              <option value="grid">격자 (Grid)</option>
            </select>
            <small class="p-field__hint">
              항목별로 플레이스홀더 표시/숨김을 개별 설정할 수 있습니다
            </small>
          </div>
          <div class="p-field">
            <label class="p-field__label">플레이스홀더 투명도 (Opacity)</label>
            <div class="pg-hand__range">
              <input
                v-model.number="sectionData.content.placeholderOpacity"
                type="range"
                class="pg-hand__range-input"
                min="0"
                max="100"
                step="5"
              />
              <span class="pg-hand__range-value">{{ sectionData.content.placeholderOpacity }}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Handwriting Items -->
    <div class="pg-hand__section">
      <div class="pg-hand__section-label">손글씨 항목 (Handwriting Items)</div>

      <div class="pg-hand__list" v-if="sectionData.content.items && sectionData.content.items.length > 0">
        <div
          v-for="(item, index) in sectionData.content.items"
          :key="index"
          class="pg-hand__item"
        >
          <div class="pg-hand__item-head">
            <span class="pg-hand__index">{{ index + 1 }}</span>
            <div class="pg-hand__item-actions">
              <button
                type="button"
                class="pg-hand__icon-btn pg-hand__icon-btn--danger"
                title="삭제"
                :disabled="sectionData.content.items.length <= 1"
                @click.prevent="removeItem(index)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>

          <div class="pg-hand__item-body">
            <div class="pg-hand__char">
              <label class="p-field__label">작성할 글자 (Character)</label>
              <input
                v-model="item.character"
                type="text"
                class="p-input pg-hand__char-input"
                placeholder="가"
              />
            </div>
            <div class="pg-hand__fields">
              <div class="p-field">
                <label class="p-field__label">힌트 (Hint)</label>
                <textarea
                  v-model="item.hint"
                  class="p-input p-input--area"
                  rows="2"
                  placeholder="글자 작성에 대한 힌트를 입력하세요 (예: 'ㄱ'과 'ㅏ'를 합쳐서 씁니다)"
                />
              </div>
              <div class="p-form-grid p-form-grid--3">
                <div class="p-field">
                  <label class="p-field__label">발음 / 읽기</label>
                  <input
                    v-model="item.pronunciation"
                    type="text"
                    class="p-input"
                    placeholder="ga"
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">의미 (Meaning)</label>
                  <input
                    v-model="item.meaning"
                    type="text"
                    class="p-input"
                    placeholder="First syllable of the Korean alphabet"
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">플레이스홀더 표시</label>
                  <label class="pg-hand__switch">
                    <input
                      v-model="item.showPlaceholder"
                      type="checkbox"
                    />
                    <span class="pg-hand__switch-track"></span>
                    <span class="pg-hand__switch-label">{{ item.showPlaceholder ? '표시' : '숨김' }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else
        class="pg-hand__empty"
      >
        <i class="material-icons-round">gesture</i>
        <p>손글씨 항목이 없습니다</p>
      </div>

      <div class="pg-hand__actions">
        <ABtn kind="yellow" icon="add" @click.prevent="addItem">항목 추가</ABtn>
      </div>
    </div>

    <!-- Preview -->
    <div class="pg-hand__section" v-if="sectionData.content.items && sectionData.content.items.length > 0">
      <div class="pg-hand__section-label">미리보기 (Preview)</div>
      <div class="pg-hand__preview-meta">
        <i class="material-icons-round">info</i>
        <span>
          <strong>현재 플레이스홀더 설정:</strong>
          {{ getPlaceholderStyleLabel(sectionData.content.placeholderStyle) }}
          (투명도: {{ sectionData.content.placeholderOpacity }}%)
        </span>
      </div>
      <div class="pg-hand__preview-grid">
        <div
          v-for="(item, index) in sectionData.content.items"
          :key="'preview-' + index"
          :class="['pg-hand__preview-card', getPlaceholderClass(item)]"
        >
          <div class="pg-hand__preview-char-wrap">
            <div
              v-if="item.showPlaceholder"
              class="pg-hand__preview-placeholder"
              :style="getPlaceholderStyle()"
            >
              {{ item.character || '?' }}
            </div>
            <div class="pg-hand__preview-char">{{ item.character || '?' }}</div>
          </div>
          <div class="pg-hand__preview-pron" v-if="item.pronunciation">{{ item.pronunciation }}</div>
          <div class="pg-hand__preview-hint" v-if="item.hint">{{ item.hint }}</div>
          <div class="pg-hand__preview-meaning" v-if="item.meaning">{{ item.meaning }}</div>
          <div class="pg-hand__preview-badge" v-if="!item.showPlaceholder">
            <span class="pg-hand__pill">Placeholder OFF</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineModel, onMounted, watch } from 'vue';
import ABtn from '@/components/Pineapple/ABtn.vue';

const sectionData = defineModel({ type: Object, required: true });

const initializeContent = () => {
  if (!sectionData.value) {
    sectionData.value = {};
  }

  if (!sectionData.value.content) {
    sectionData.value.content = {};
  }

  // Initialize practiceType
  if (!sectionData.value.content.practiceType) {
    sectionData.value.content.practiceType = 'handwriting';
  }

  // Initialize instructions
  if (!sectionData.value.content.instructions) {
    sectionData.value.content.instructions = '';
  }

  // Initialize placeholder settings
  if (!sectionData.value.content.placeholderStyle) {
    sectionData.value.content.placeholderStyle = 'light';
  }

  if (sectionData.value.content.placeholderOpacity === undefined) {
    sectionData.value.content.placeholderOpacity = 30;
  }

  // Initialize items array
  if (!sectionData.value.content.items || !Array.isArray(sectionData.value.content.items)) {
    sectionData.value.content.items = [
      { character: '', hint: '', pronunciation: '', meaning: '', showPlaceholder: true }
    ];
  } else {
    // Ensure all existing items have showPlaceholder property
    sectionData.value.content.items.forEach(item => {
      if (item.showPlaceholder === undefined) {
        item.showPlaceholder = true;
      }
    });
  }
};

// Add new handwriting item
const addItem = () => {
  if (!sectionData.value.content.items) {
    sectionData.value.content.items = [];
  }
  sectionData.value.content.items.push({
    character: '',
    hint: '',
    pronunciation: '',
    meaning: '',
    showPlaceholder: true
  });
};

// Remove handwriting item
const removeItem = (index) => {
  if (sectionData.value.content.items.length > 1) {
    sectionData.value.content.items.splice(index, 1);
  }
};

// Get placeholder style label
const getPlaceholderStyleLabel = (style) => {
  const labels = {
    outline: '외곽선',
    dotted: '점선',
    light: '연한 글자',
    grid: '격자'
  };
  return labels[style] || '연한 글자';
};

// Get placeholder class based on style
const getPlaceholderClass = (item) => {
  if (!item.showPlaceholder) {
    return '';
  }
  return `placeholder-${sectionData.value.content.placeholderStyle}`;
};

// Get placeholder inline styles
const getPlaceholderStyle = () => {
  const opacity = (sectionData.value.content.placeholderOpacity || 30) / 100;
  return {
    opacity: opacity
  };
};

// Initialize immediately
initializeContent();

// Watch for changes in sectionData
watch(() => sectionData.value, () => {
  if (!sectionData.value?.content?.items) {
    initializeContent();
  }
}, { immediate: true, deep: true });

onMounted(() => {
  initializeContent();
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-hand {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__section-label {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    letter-spacing: 0.02em;
    padding-bottom: 6px;
    border-bottom: 1px dashed $p-line;
  }

  &__settings {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
  }

  &__range {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 38px;
  }

  &__range-input {
    flex: 1;
    height: 6px;
    -webkit-appearance: none;
    appearance: none;
    background: $p-line;
    border-radius: 999px;
    outline: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: $p-point;
      border: 2px solid $p-ink;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: $p-point;
      border: 2px solid $p-ink;
      cursor: pointer;
    }
  }

  &__range-value {
    min-width: 50px;
    text-align: center;
    background: $p-ink;
    color: $p-point;
    padding: 4px 10px;
    border-radius: 999px;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 700;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    min-width: 28px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 800;
    display: inline-grid;
    place-items: center;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &--danger {
      color: $p-err;
      &:hover:not(:disabled) { background: $p-err-soft; }
    }
  }

  &__item-body {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 16px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__char {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__char-input {
    text-align: center;
    font-size: 24px;
    font-weight: 700;
    height: 64px;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__switch {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    height: 38px;

    input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }

    &-track {
      position: relative;
      width: 36px;
      height: 20px;
      background: $p-line;
      border-radius: 999px;
      transition: background $p-dur $p-ease;

      &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: $p-panel;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        transition: transform $p-dur $p-ease;
      }
    }

    input[type="checkbox"]:checked + &-track {
      background: $p-point-deep;

      &::after {
        transform: translateX(16px);
      }
    }

    &-label {
      font-size: 13px;
      color: $p-ink-mid;
      font-weight: 600;
    }
  }

  &__empty {
    background: $p-panel;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    padding: 32px 16px;
    text-align: center;
    color: $p-ink-soft;

    > i {
      font-size: 48px;
      color: $p-ink-soft;
    }

    p {
      margin: 8px 0 0;
      font-size: 13px;
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__preview-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: $p-panel-alt;
    border-radius: $p-radius-sm;
    border-left: 3px solid $p-info;
    font-size: 12px;
    color: $p-ink-mid;

    > i {
      color: $p-info;
      font-size: 16px;
    }

    strong {
      color: $p-ink;
    }
  }

  &__preview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }

  &__preview-card {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 16px;
    text-align: center;
    position: relative;
    transition: border-color $p-dur $p-ease, transform $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
      transform: translateY(-2px);
    }
  }

  &__preview-char-wrap {
    position: relative;
    display: inline-block;
    margin-bottom: 10px;
  }

  &__preview-char {
    font-size: 48px;
    font-weight: 700;
    color: $p-ink;
    line-height: 1;
    position: relative;
    z-index: 2;
    font-family: $p-font-body;
  }

  &__preview-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    font-size: 48px;
    font-weight: 700;
    line-height: 1;
    z-index: 1;
    pointer-events: none;
    font-family: $p-font-body;
  }

  /* Outline style */
  .placeholder-outline & {
    &__preview-placeholder {
      color: transparent;
      -webkit-text-stroke: 2px $p-point-deep;
    }
    &__preview-char { color: transparent; }
  }

  /* Dotted style */
  .placeholder-dotted & {
    &__preview-placeholder {
      color: $p-point-deep;
      text-shadow:
        0 0 1px $p-point-deep,
        1px 1px 0 transparent,
        2px 2px 0 $p-point-deep,
        3px 3px 0 transparent,
        4px 4px 0 $p-point-deep;
    }
    &__preview-char { color: transparent; }
  }

  /* Light style */
  .placeholder-light & {
    &__preview-placeholder { color: $p-point-deep; }
    &__preview-char { color: transparent; }
  }

  /* Grid style */
  .placeholder-grid {
    background-image:
      linear-gradient(to right, rgba(201, 138, 10, 0.12) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(201, 138, 10, 0.12) 1px, transparent 1px);
    background-size: 10px 10px;

    .pg-hand__preview-placeholder { color: $p-point-deep; }
    .pg-hand__preview-char { color: transparent; }
  }

  &__preview-pron {
    font-size: 14px;
    color: $p-point-deep;
    font-weight: 700;
    margin-bottom: 4px;
    font-family: $p-font-mono;
  }

  &__preview-hint {
    font-size: 11px;
    color: $p-ink-mid;
    margin-bottom: 4px;
    font-style: italic;
  }

  &__preview-meaning {
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__preview-badge {
    position: absolute;
    top: 8px;
    right: 8px;
  }

  &__pill {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 999px;
    background: $p-ink-soft;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    font-family: $p-font-mono;
  }
}
</style>
